// SAD-999: the Command Center's filters live in the URL, so a view can be
// shared, survives back/forward, and every panel reads the same parsed
// filter. One parser, one serializer, one request builder.
import { ContractModelTypes } from '@mono/proto-clients';
export const EMPTY_FILTER = {
    scope: 'mine',
    programs: [],
    teams: [],
    owners: [],
    states: [],
    period: 'current',
    due: '',
    from: '',
    to: '',
    portfolio: '',
    sector: ''
};
/** The deliverable states the Status filter offers, in the words a user reads. */ export const STATUS_OPTIONS = [
    {
        value: 'late',
        label: 'Late'
    },
    {
        value: 'at_risk',
        label: 'At risk'
    },
    {
        value: 'due_soon',
        label: 'Due in 14 days'
    },
    {
        value: 'not_yet_due',
        label: 'Not yet due'
    },
    {
        value: 'awaiting_review',
        label: 'Waiting on approval'
    },
    {
        value: 'rejected',
        label: 'Returned'
    },
    {
        value: 'upcoming',
        label: 'Upcoming'
    },
    {
        value: 'approved',
        label: 'Approved'
    },
    {
        value: 'on_hold',
        label: 'Paused (program on hold)'
    }
];
export const DUE_OPTIONS = [
    {
        value: '7d',
        label: 'Next 7 days',
        days: 7
    },
    {
        value: '14d',
        label: 'Next 14 days',
        days: 14
    },
    {
        value: '30d',
        label: 'Next 30 days',
        days: 30
    }
];
/**
 * The status filters a chart's drill sets as a group, by the one word the
 * chart uses: a status-by-team bar's "Filed" segment, and a calendar day's
 * "late or still owed". Keyed by the states sorted.
 */ export const FILED_STATES = [
    'approved',
    'awaiting_review',
    'rejected'
];
export const OWED_STATES = [
    'awaiting_review',
    'due_soon',
    'late',
    'rejected',
    'upcoming'
];
const STATE_GROUPS = {
    [FILED_STATES.join(',')]: 'Filed',
    [OWED_STATES.join(',')]: 'Late or still owed'
};
/** The Status filter's words for a set of states. */ export function statesLabel(states) {
    if (states.length === 0) return 'Any status';
    const key = [
        ...states
    ].sort().join(',');
    if (STATE_GROUPS[key]) return STATE_GROUPS[key];
    return states.map((s)=>STATUS_OPTIONS.find((o)=>o.value === s)?.label ?? s).join(' or ');
}
/** "-" is the owner of a date with neither a team owner nor a program lead. */ export const NO_OWNER = '-';
const KNOWN_STATES = new Set(STATUS_OPTIONS.map((o)=>o.value));
const ID = /^[A-Za-z0-9_-]{1,64}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
function list(params, key, keep) {
    const raw = params.get(key) ?? '';
    return [
        ...new Set(raw.split(',').map((v)=>v.trim()).filter((v)=>v && keep(v)))
    ];
}
/** Reads the filter from the URL; anything unrecognised is dropped, never trusted. */ export function parseFilter(params) {
    const due = params.get('due') ?? '';
    const one = (key)=>{
        const v = params.get(key) ?? '';
        return ID.test(v) ? v : '';
    };
    const day = (key)=>{
        const v = params.get(key) ?? '';
        return DAY.test(v) && !Number.isNaN(Date.parse(v)) ? v : '';
    };
    let from = day('from');
    let to = day('to');
    if (from && to && to < from) [from, to] = [
        '',
        ''
    ];
    return {
        scope: params.get('scope') === 'portfolio' ? 'portfolio' : 'mine',
        programs: list(params, 'program', (v)=>ID.test(v)),
        teams: list(params, 'team', (v)=>ID.test(v)),
        owners: list(params, 'owner', (v)=>v === NO_OWNER || ID.test(v)),
        states: list(params, 'status', (v)=>KNOWN_STATES.has(v)),
        period: params.get('period') === 'all' ? 'all' : 'current',
        due: DUE_OPTIONS.some((o)=>o.value === due) ? due : '',
        from,
        to,
        portfolio: one('portfolio'),
        sector: one('sector')
    };
}
/**
 * Writes the filter into the URL, keeping any parameter the filter does not
 * own. Defaults are left out, so the default view is the bare path.
 */ export function writeFilter(filter, base) {
    const out = new URLSearchParams(base);
    const set = (key, value)=>value ? out.set(key, value) : out.delete(key);
    set('scope', filter.scope === 'portfolio' ? 'portfolio' : '');
    set('program', filter.programs.join(','));
    set('team', filter.teams.join(','));
    set('owner', filter.owners.join(','));
    set('status', filter.states.join(','));
    set('period', filter.period === 'all' ? 'all' : '');
    set('due', filter.due);
    set('from', filter.from);
    set('to', filter.to);
    set('portfolio', filter.portfolio);
    set('sector', filter.sector);
    return out;
}
export function isDefaultFilter(f) {
    return writeFilter(f).toString() === '';
}
export const VIEW_OPTIONS = [
    {
        value: 'mine',
        label: 'My programs'
    },
    {
        value: 'portfolio',
        label: 'Whole portfolio'
    },
    {
        value: 'week',
        label: 'This week'
    }
];
/** A query string as the filter writes it, so two spellings of one filter compare equal. */ export function normalQuery(query) {
    return writeFilter(parseFilter(new URLSearchParams(query))).toString();
}
export function viewOf(f, saved = []) {
    if (isDefaultFilter(f)) return 'mine';
    if (isDefaultFilter({
        ...f,
        scope: 'mine'
    }) && f.scope === 'portfolio') return 'portfolio';
    if (isDefaultFilter({
        ...f,
        due: ''
    }) && f.due === '7d') return 'week';
    const q = writeFilter(f).toString();
    const hit = saved.find((v)=>normalQuery(v.query) === q);
    return hit ? `saved:${hit.id}` : 'custom';
}
export function filterOfView(view) {
    if (view === 'week') return {
        ...EMPTY_FILTER,
        due: '7d'
    };
    if (view === 'portfolio') return {
        ...EMPTY_FILTER,
        scope: 'portfolio'
    };
    return EMPTY_FILTER;
}
/** A calendar date as YYYY-MM-DD in local time. */ export function isoDay(d) {
    const p = (n)=>String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
/** The pangaea body the filter asks for (ryugu narrows program_ids to what the viewer can see). */ export function summaryBody(f, today) {
    const ranged = !!(f.from || f.to);
    const due = ranged ? undefined : DUE_OPTIONS.find((o)=>o.value === f.due);
    const to = due ? new Date(today.getFullYear(), today.getMonth(), today.getDate() + due.days) : null;
    return new ContractModelTypes.GetPortfolioDeliverySummaryRequest({
        mine: f.scope === 'mine',
        programIds: f.programs,
        workstreamIds: f.teams,
        ownerContactIds: f.owners,
        states: f.states,
        window: f.period === 'all' ? ContractModelTypes.OccurrenceWindow.ALL : ContractModelTypes.OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        dueFrom: ranged ? f.from : due ? isoDay(today) : '',
        dueTo: ranged ? f.to : to ? isoDay(to) : '',
        portfolioId: f.portfolio,
        sectorId: f.sector
    });
}
